// Clubnamen uit een importbestand koppelen aan het clubregister (stap 2, A1).
// Puur: geen DOM, geen database. Getoetst in tests/club-match.mjs.
//
// Per schrijfwijze in het bestand één uitkomst:
//   exact     zelfde naam (los van hoofdletters, accenten en leestekens), naam met
//             plaats, ref uit het register of een eerder bevestigde alias. Koppelt
//             automatisch.
//   voorstel  één club lijkt duidelijk het meest ("Dragon TKD" en "Dragon
//             Taekwon-Do Groningen"). De beheerder bevestigt.
//   keuze     meer clubs lijken even goed. De beheerder kiest.
//   onbekend  niets lijkt genoeg. Gaat naar de werklijst, de import stopt niet.
//
// Liever een vraag dan een gok: een foute club op een lid is lastiger te
// herstellen dan een lege.

const plat = s => String(s ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/&/g, ' en ').replace(/[.'’]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();

// Woorden die in bijna elke clubnaam staan en dus niets onderscheiden.
const VULWOORDEN = new Set(['taekwondo', 'taekwon', 'do', 'tkd', 'itf', 'wtf', 'sportschool', 'sportvereniging', 'vereniging',
  'ver', 'sv', 'club', 'dojang', 'school', 'stichting', 'academy', 'academie', 'de', 'het', 'en', 'van', 'te', 'in']);

export const normaliseer = plat;
export const kern = s => plat(s).split(' ').filter(w => w && !VULWOORDEN.has(w));

function bigrammen(s) {
  const t = ` ${s} `;
  const uit = new Map();
  for (let i = 0; i < t.length - 1; i++) { const b = t.slice(i, i + 2); uit.set(b, (uit.get(b) || 0) + 1); }
  return uit;
}
// Dice-coëfficiënt op lettertweetallen: 1 is gelijk, 0 is niets gemeen.
export function gelijkenis(a, b) {
  if (!a || !b) return 0;
  if (a === b) return 1;
  const x = bigrammen(a); const y = bigrammen(b);
  let gedeeld = 0; let totaal = 0;
  for (const n of x.values()) totaal += n;
  for (const [k, n] of y) { totaal += n; gedeeld += Math.min(n, x.get(k) || 0); }
  return (2 * gedeeld) / totaal;
}

// Hoe goed lijkt de waarde op deze club (0 tot 1), op de kernwoorden.
function score(waardeKern, club) {
  const naamKern = kern(club.naam);
  if (!waardeKern.length || !naamKern.length) return 0;
  const plaats = kern(club.plaats || '');
  // Een plaatsnaam in de waarde die niet bij deze club hoort, telt niet als gelijkenis.
  const zonderPlaats = waardeKern.filter(w => !plaats.includes(w));
  const w = zonderPlaats.length ? zonderPlaats : waardeKern;
  const n = naamKern.filter(x => !plaats.includes(x));
  const naam = n.length ? n : naamKern;
  const deel = w.every(x => naam.includes(x)) || naam.every(x => w.includes(x));
  const s = gelijkenis(w.join(' '), naam.join(' '));
  const metPlaats = plaats.length && plaats.every(x => waardeKern.includes(x)) ? 0.1 : 0;
  return Math.min(1, Math.max(s, deel ? 0.8 : 0) + metPlaats);
}

const DREMPEL = 0.72;  // daaronder geen voorstel
const MARGE = 0.1;     // zoveel beter dan de tweede, anders kiest de beheerder

// waarden: schrijfwijzen uit het bestand. register: [{ id, naam, plaats, ref }].
// aliassen: [{ entity_id, value }] uit external_ids (systeem naam).
export function koppelClubs(waarden, register, aliassen = []) {
  const opNaam = new Map();
  for (const c of register) {
    for (const sleutel of [plat(c.naam), plat(`${c.naam} ${c.plaats || ''}`), plat(c.ref)]) {
      if (sleutel && !opNaam.has(sleutel)) opNaam.set(sleutel, c.id);
    }
  }
  const actief = new Set(register.map(c => c.id));
  for (const a of aliassen) {
    const sleutel = plat(a.value);
    if (sleutel && actief.has(a.entity_id) && !opNaam.has(sleutel)) opNaam.set(sleutel, a.entity_id);
  }

  const uit = new Map();
  for (const ruw of waarden) {
    const waarde = String(ruw ?? '').trim();
    if (uit.has(waarde)) { uit.get(waarde).aantal++; continue; }
    const regel = { waarde, aantal: 1, status: 'onbekend', club_id: null, kandidaten: [] };
    uit.set(waarde, regel);
    if (!waarde) continue;
    const exact = opNaam.get(plat(waarde));
    if (exact) { regel.status = 'exact'; regel.club_id = exact; continue; }
    const k = kern(waarde);
    const rang = register.map(c => ({ id: c.id, score: score(k, c) }))
      .filter(r => r.score >= DREMPEL).sort((a, b) => b.score - a.score);
    regel.kandidaten = rang.slice(0, 3).map(r => r.id);
    if (!rang.length) continue;
    if (rang.length === 1 || rang[0].score - rang[1].score >= MARGE) {
      regel.status = 'voorstel'; regel.club_id = rang[0].id;
    } else {
      regel.status = 'keuze';
    }
  }
  return [...uit.values()];
}
